// The roadmap: each department's opportunities ranked by value a year. A person starts one and it goes through the
// improvement cycle's three steps; the board below shows what's at Plan, Do and Check now.
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import { factory, CYCLE } from "../factory/factoryApi";
import { imp, money, type RoadmapItem } from "./impApi";
import { Head, Kpis, Win } from "./SerKit";
import "./ser.css";

const deptKey = (x: RoadmapItem) => x.area ?? "";

export function RoadmapPage() {
  const [sp, setSp] = useSearchParams();
  const dept = sp.get("dept");
  const [tick, setTick] = useState(0);
  const r = useLoad(() => imp.roadmap(), [tick]);
  const rules = useLoad(() => factory.rules());
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const d = r.data;
  const onRoadmap = useMemo(() => (d?.items ?? []).filter((x) => x.state === "roadmap").sort((a, b) => (b.valuePerYear ?? -1) - (a.valuePerYear ?? -1)), [d]);
  const inCycle = (d?.items ?? []).filter((x) => x.state !== "roadmap");
  const depts = useMemo(() => {
    const m = new Map<string, { key: string; name: string; value: number; n: number }>();
    for (const x of onRoadmap) { const k = deptKey(x); const g = m.get(k) ?? { key: k, name: x.areaName ?? "No department", value: 0, n: 0 }; g.value += x.valuePerYear ?? 0; g.n++; m.set(k, g); }
    return [...m.values()].sort((a, b) => b.value - a.value);
  }, [onRoadmap]);
  const shown = dept === null ? onRoadmap : onRoadmap.filter((x) => deptKey(x) === dept);
  const sumOf = (xs: RoadmapItem[]) => xs.reduce((t, x) => t + (x.valuePerYear ?? 0), 0);
  const start = async (x: RoadmapItem) => {
    setErr(null); setNote(null);
    try { await factory.start(x.number); setNote(`${x.number} started. Write its plan on the A3.`); setTick((t) => t + 1); } catch (e) { setErr(e); }
  };
  const keptValue = d ? d.keptThisYear.reduce((t, x) => t + x.valuePerYear, 0) : 0;
  const does = Object.fromEntries((rules.data?.steps ?? []).map((s) => [s.id, s.does]));
  return (
    <>
      <CommandBar status={d ? `${onRoadmap.length} on the roadmap · ${inCycle.length} in the cycle` : "Loading…"}>
        <Link className="cmd primary" to="/factory/new?start=roadmap">Add an opportunity</Link>
        <Link className="cmd" to="/improve/catalog">Process catalog</Link>
        <Link className="cmd" to="/factory">Implementation Factory</Link>
      </CommandBar>
      <div className="page ser">
        <Head kicker="Improvement · Roadmap" title="What to improve next"
          lede="Each department's opportunities, ranked by value a year. Start one and it goes through Plan, Do and Check on one A3." />
        <ErrorBox error={r.error ?? err} />
        {note && <div className="msg info">{note}</div>}
        <Kpis items={[
          { label: "On the roadmap", value: d ? money(sumOf(onRoadmap)) : "–", sub: d ? `${onRoadmap.length} opportunit${onRoadmap.length === 1 ? "y" : "ies"}, value a year` : "" },
          { label: "In the cycle", value: d ? money(sumOf(inCycle)) : "–", sub: d ? `${inCycle.length} request${inCycle.length === 1 ? "" : "s"}` : "" },
          { label: "Kept this year", value: d ? money(keptValue) : "–", sub: d ? `${d.keptThisYear.reduce((t, x) => t + x.count, 0)} kept, value a year` : "", tone: keptValue > 0 ? "up" : undefined },
        ]} />

        <Win title="Roadmap" sub="ranked by value a year">
          {depts.length > 1 && (
            <div className="chips" style={{ padding: "10px 16px", borderBottom: "1px solid var(--s-border)" }} role="group" aria-label="Department">
              <button className="chipbtn" aria-pressed={dept === null} onClick={() => setSp({}, { replace: true })}>All departments</button>
              {depts.map((g) => <button key={g.key} className="chipbtn" aria-pressed={dept === g.key} onClick={() => setSp({ dept: g.key }, { replace: true })}>{g.name} · {g.n}</button>)}
            </div>)}
          {!d ? <div className="empty">Loading…</div> : !shown.length ? (
            <div className="empty">Nothing is on the roadmap. <Link to="/factory/new?start=roadmap">Add an opportunity</Link>, or put one on the roadmap from a process in the <Link to="/improve/catalog">catalog</Link>.</div>
          ) : (
            <ol className="opps" style={{ padding: 16 }}>{shown.map((x, i) => (
              <li key={x.number} className="opp">
                <span className="opp-rank">{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <Link className="opp-t" to={`/factory/r/${x.number}`}>{x.title}</Link>
                  <div className="opp-m"><span>{[x.number, x.areaName, x.process ? `${x.process.number} ${x.process.name}` : null].filter(Boolean).join(" · ")}</span></div>
                </div>
                <div className="opp-side">
                  <span className="opp-v">{money(x.valuePerYear)}</span>
                  <button className="mini p" onClick={() => start(x)} aria-label={`Start ${x.number}`}>Start</button>
                </div>
              </li>))}
            </ol>)}
        </Win>

        <h2 className="s-h2">Plan, Do, Check</h2>
        <div className="pdc" role="list" aria-label="Requests by step">
          {CYCLE.map((s) => {
            const xs = inCycle.filter((x) => x.step === s.id);
            return (
              <section key={s.id} className="win pdc-col" role="listitem">
                <div className="win-bar"><div className="win-title">{s.name}</div><span className="tag">{xs.length}</span></div>
                {does[s.id] && <p className="pdc-d">{does[s.id]}</p>}
                {!xs.length ? <p className="pdc-e">Nothing here now.</p> : (
                  <ul className="pdc-list">{xs.map((x) => (
                    <li key={x.number}><Link to={`/factory/r/${x.number}`}>{x.title}</Link><span>{[x.number, x.areaName].filter(Boolean).join(" · ")}<b>{money(x.valuePerYear)}</b></span></li>))}
                  </ul>)}
              </section>);
          })}
        </div>
      </div>
    </>
  );
}
